'use client';

import { FC, useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import { useCompetitionContext } from "../../providers/CompetitionContext";
import { useTeamContext } from '../../providers/TeamContext';
import { Player } from '@repo/types';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { DriverCard } from "./driver-card";
import { PlayerList } from "./player-list";
import { LoadingSpinner } from "../../components/loading-spinner";
import { Flag, Bus, Users, User, Save, ChevronDown, Loader2, Clover } from 'lucide-react';
import { setUsername } from "../../actions/onboarding";
import { chunkByRowSizes, generateBenchRowSizes, generatePyramidRowSizes } from './row-layout';
import moment from 'moment';
import 'moment/locale/fr';
import {
  parseTeamName,
  sanitizeTeamNameInput,
  TEAM_NAME_MAX_LENGTH,
} from '@repo/api/team-name';
moment.locale('fr');

const USERNAME_REGEX =
  /^[A-Za-z0-9àâäæçéèêëîïôœöùûüÿñÀÂÄÆÇÉÈÊËÎÏÔŒÖÙÛÜŸÑ_.-]+$/;
const USERNAME_MIN_LENGTH = 4;
const USERNAME_MAX_LENGTH = 32;

interface NewTeamProps {
  weekId: number;
  user: {
    email?: string, 
    name?: string, 
    token?: any
  } | null;
}

const NewTeam: FC<NewTeamProps> = ({ weekId, user }) => {
  const PLAYER_LIST_ID = 'fantasy-player-list';
  const { competitionConfig, competitionData } = useCompetitionContext();
  const allowEmptyDataForDesign = process.env.NEXT_PUBLIC_FANTASY_ALLOW_EMPTY_DATA === 'true';
  const clubs = competitionData?.clubs ?? [];
  const players = competitionData?.players ?? [];

  const { 
    team, 
    removeDriverFromTeam, 
    availableBudget, 
    saveTeam,
    isSaving,
    setTeamName, 
    driverSelectionCheck, 
    addDriverToTeam,
    setCaptain,
    isLoading
  } = useTeamContext();

  const { data: session, update: updateSession } = useSession();
  const sessionUsername = session?.user?.username ?? '';

  const [teamNameLocal, setTeamNameLocal] = useState('');
  const [usernameLocal, setUsernameLocal] = useState('');
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [teamNameError, setTeamNameError] = useState<string | null>(null);

  useEffect(() => {
    if (!team) return;
    setTeamNameLocal(team.teamName ?? '');
  }, [team?.teamName, team]);

  useEffect(() => {
    if (sessionUsername) setUsernameLocal(sessionUsername);
  }, [sessionUsername]);

  const validateUsername = (value: string): string | null => {
    const trimmed = value.trim();
    if (trimmed.length < USERNAME_MIN_LENGTH) {
      return `Le nom d'utilisateur doit contenir au moins ${USERNAME_MIN_LENGTH} caractères.`;
    }
    if (trimmed.length > USERNAME_MAX_LENGTH) {
      return `Le nom d'utilisateur ne peut pas dépasser ${USERNAME_MAX_LENGTH} caractères.`;
    }
    if (!USERNAME_REGEX.test(trimmed)) {
      return "Lettres (accents acceptés), chiffres, points, tirets et underscores.";
    }
    return null;
  };

  const handleCreateTeam = async () => {
    const parsedTeamName = parseTeamName(teamNameLocal);
    if (!parsedTeamName.ok) {
      setTeamNameError(parsedTeamName.message);
      return;
    }
    setTeamNameError(null);
    setTeamNameLocal(parsedTeamName.value);
    setTeamName(parsedTeamName.value);

    const trimmedUsername = usernameLocal.normalize('NFC').trim();
    const validation = validateUsername(trimmedUsername);
    if (validation) {
      setUsernameError(validation);
      return;
    }
    setUsernameError(null);

    if (trimmedUsername !== sessionUsername) {
      const result = await setUsername(trimmedUsername);
      if (!result.ok) {
        setUsernameError(result.message);
        return;
      }
      await updateSession({});
    }

    saveTeam();
  };

  const formatCurrencyInMillions = (amount: number) => (
    `${(amount / 1000000).toLocaleString('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    })}M`
  );

  const lineupSize = competitionConfig?.lineupSize ?? 0;
  const benchSize = competitionConfig?.benchSize ?? 0;
  const hasBench = benchSize > 0;
  const shouldVerticallyCenterStarting = lineupSize === 15 && !hasBench;

  // Calculate total selections needed
  const totalSelections = lineupSize + benchSize;

  // Current selected drivers
  const selectedDrivers = [...(team?.starting ?? []), ...(team?.bench ?? [])].map((driver: any) => ({
    ...driver,
    club: clubs.find((club: any) => club.id === driver.clubId)
  }));
  const leaderCandidates = [...(team?.starting ?? [])]
    .map((driver: any) => ({
      ...driver,
      club: clubs.find((club: any) => club.id === driver.clubId)
    }))
    .sort((a: any, b: any) => b.cap - a.cap);
  const hasLeaderCandidates = leaderCandidates.length > 0;
  const isLeaderSelectionEnabled =
    competitionConfig?.captain === true &&
    competitionConfig?.captainBooster !== true;

  const startingSlots = [
    ...(team?.starting ?? []),
    ...Array(Math.max(lineupSize - (team?.starting?.length ?? 0), 0)).fill(undefined),
  ];

  const benchSlots = hasBench ? [
    ...(team?.bench ?? []),
    ...Array(Math.max(benchSize - (team?.bench?.length ?? 0), 0)).fill(undefined),
  ] : [];

  const startingRowSizes = lineupSize === 12 ? [1, 3, 4, 4] : generatePyramidRowSizes(lineupSize);
  const benchRowSizes = hasBench ? generateBenchRowSizes(benchSize) : [];
  const startingRowChunks = chunkByRowSizes(startingSlots, startingRowSizes);
  const benchRowChunks = hasBench ? chunkByRowSizes(benchSlots, benchRowSizes) : [];

  const handleRemoveDriver = (driver: Player) => {
    removeDriverFromTeam(driver);
  };

  const handleDriverSelection = (driver: Player) => {
    addDriverToTeam(driver);
  };

  const handleLeaderSelection = (leader: Player) => {
    setCaptain(leader);
  };

  useEffect(() => {
    if (!isLeaderSelectionEnabled) return;
    if ((team?.starting?.length ?? 0) !== 1) return;
    const only = team?.starting?.[0];
    if (!only) return;
    if (team?.starting?.some((d) => d.cap === 1)) return;
    setCaptain(only);
  }, [isLeaderSelectionEnabled, setCaptain, team?.starting]);

  const scrollToPlayerList = () => {
    document.getElementById(PLAYER_LIST_ID)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  const missingRequiredData = !competitionConfig || !competitionData?.clubs || !competitionData?.players;

  if (team == null || isLoading || (!allowEmptyDataForDesign && missingRequiredData)) {
    return <LoadingSpinner />;
  }

  const hasCompleteRoster = selectedDrivers.length >= totalSelections;
  const teamNameValidation = parseTeamName(teamNameLocal);
  const usernameValidationMessage = validateUsername(usernameLocal);
  const saveDisabled =
    !teamNameValidation.ok ||
    usernameValidationMessage !== null ||
    !hasCompleteRoster ||
    isSaving;

  // Show a live error as soon as the user has typed something invalid, so they
  // understand why the save button is disabled instead of being silently blocked.
  const teamNameDisplayError =
    teamNameError ??
    (teamNameLocal.trim().length > 0 && !teamNameValidation.ok
      ? teamNameValidation.message
      : null);
  const usernameDisplayError =
    usernameError ??
    (usernameLocal.trim().length > 0 && usernameValidationMessage !== null
      ? usernameValidationMessage
      : null);
  const noStressDescription = (() => {
    const deadlineDate = competitionData?.deadlineInfo?.deadlineDate;
    if (!deadlineDate) {
      return "Tu peux effectuer des changements illimités dans ton équipe jusqu'au départ de la première course.";
    }

    const deadlineMoment = moment.utc(deadlineDate).local();
    return `Tu peux encore modifier ton équipe jusqu'au ${deadlineMoment.format('ddd DD/MM')} à ${deadlineMoment.format('LT')}.`;
  })();

  return (
    <>
      <div className="grid grid-cols-1 gap-4 pb-36 md:grid-cols-2 md:items-start md:pb-28">
        {/* Left column */}
        <div className="overflow-hidden rounded-[0.875rem] bg-white shadow-sm">
          <div className="bg-[rgb(0_61_96)] px-5 py-4 text-white">
            <h1 className="font-extra">Crée ton équipe</h1>
          </div>
          <div className="m-4 overflow-hidden rounded-[0.875rem] bg-gpspel-grey">
            <div className="px-4 py-3">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-[rgb(0_61_96)] flex-shrink-0" />
                <Label htmlFor="teamName" className="font-bold text-sm m-0">
                  Nom de l'équipe
                </Label>
              </div>
              <Input
                id="teamName"
                onChange={(e) => {
                  const nextValue = sanitizeTeamNameInput(e.target.value);
                  setTeamName(nextValue);
                  setTeamNameLocal(nextValue);
                  if (teamNameError) setTeamNameError(null);
                }}
                type="text"
                value={teamNameLocal}
                maxLength={TEAM_NAME_MAX_LENGTH}
                placeholder="Choisi un nom pour ton équipe cycliste"
                className="bg-white"
              />
              <p className="mt-1 text-[11px] leading-snug text-gray-500">
                Lettres (accents acceptés), chiffres, apostrophes et espaces.
              </p>
              {teamNameDisplayError && (
                <p className="mt-1 text-[11px] leading-snug text-red-600">
                  {teamNameDisplayError}
                </p>
              )}
              <div className="mt-3 flex items-center gap-2 mb-2">
                <User className="h-4 w-4 text-[rgb(0_61_96)] flex-shrink-0" />
                <Label htmlFor="username" className="font-bold text-sm m-0">
                  Ton pseudo
                </Label>
              </div>
              <Input
                id="username"
                onChange={(e) => {
                  const nextValue = e.target.value.replace(/\s+/g, '');
                  setUsernameLocal(nextValue);
                  if (usernameError) setUsernameError(null);
                }}
                type="text"
                value={usernameLocal}
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                maxLength={USERNAME_MAX_LENGTH}
                placeholder="Choisi ton pseudo pour le classement"
                className="bg-white"
              />
              <p className="mt-1 text-[11px] leading-snug text-gray-500">
                Affiché dans le classement, à côté du nom de ton équipe.
              </p>
              {usernameDisplayError && (
                <p className="mt-1 text-[11px] leading-snug text-red-600">
                  {usernameDisplayError}
                </p>
              )}
            </div>
            <div className="border-t border-gpspel-grey-dark" />
            <div className="px-4 py-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Budget restant</span>
                <span className="text-lg font-extrabold text-[rgb(0_61_96)]">
                  {formatCurrencyInMillions(availableBudget)}
                </span>
              </div>
            </div>
            <div className="border-t border-gpspel-grey-dark" />
            {isLeaderSelectionEnabled && (
              <>
                <div className="px-4 py-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Clover className="h-4 w-4 text-[rgb(0_61_96)] flex-shrink-0" />
                    <Label htmlFor="leader" className="font-bold text-sm m-0">
                      Leader
                    </Label>
                  </div>
                  <div className="relative">
                    <select
                      id="leader"
                      value={hasLeaderCandidates ? (leaderCandidates[0]?.id ?? "") : ""}
                      disabled={!hasLeaderCandidates}
                      onChange={(e) => {
                        const newLeaderId = parseInt(e.target.value);
                        const newLeader = leaderCandidates.find((driver) => driver && driver.id === newLeaderId);
                        if (newLeader) {
                          handleLeaderSelection(newLeader);
                        }
                      }}
                      className="w-full appearance-none rounded-md border border-gray-300 bg-white px-3 py-2 pr-9 text-sm text-gray-900 transition focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[rgb(0_61_96)] disabled:cursor-not-allowed disabled:text-gray-400"
                    >
                      {!hasLeaderCandidates ? (
                        <option value="" disabled>
                          Choisis d&apos;abord un partant
                        </option>
                      ) : (
                        leaderCandidates.map((driver) =>
                          driver ? (
                            <option key={driver.id} value={driver.id}>
                              {driver.short}
                            </option>
                          ) : null
                        )
                      )}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                  </div>
                  <p className="mt-2 text-[11px] leading-snug text-gray-500">
                    Le leader rapporte un bonus sur cette étape.
                  </p>
                </div>
                <div className="border-t border-gpspel-grey-dark" />
              </>
            )}
            <div className="flex flex-col gap-3 p-4">
              <div className="min-w-0">
                <div className="font-bold text-base truncate">No stress</div>
                <div className="text-xs text-gray-500 mt-0.5">{noStressDescription}</div>
              </div>
              <div className="flex shrink-0">
                <Button
                  onClick={handleCreateTeam}
                  disabled={saveDisabled}
                  variant="default"
                  className="inline-flex w-full items-center justify-center gap-2 font-extrabold text-sm rounded-md px-5 py-2 bg-[#FDC300] text-black hover:bg-yellow-300 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {isSaving ? "Enregistrement..." : "Enregistrer"}
                </Button>
              </div>
            </div>
          </div>

          <div className="px-4 pb-4 pt-2">
            {/* Starting Lineup */}
            {hasBench && (
              <div className="px-4 pb-4 flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#003d60] text-white text-xs font-bold uppercase tracking-wide px-3 py-1">
                  <Flag className="h-3.5 w-3.5" />
                  Partants
                </span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>
            )}
            <div 
              className={`mx-auto relative w-full h-auto bg-no-repeat bg-top bg-contain rounded-[0.875rem] overflow-hidden ${
                shouldVerticallyCenterStarting
                  ? 'flex min-h-[46rem] flex-col justify-center py-6'
                  : 'pt-2 pb-10'
              }`}
              style={{
                backgroundSize: "cover",
                backgroundImage: "linear-gradient(to bottom, rgba(0,61,96,0.15), rgba(0,61,96,0.3)), url('https://ocnc-fantasy.b-cdn.net/files/road.jpg')",
              }}
            >
              {startingRowChunks.map((row, rowIndex) => (
                <div 
                  key={`starting-${rowIndex}`}
                  className={`flex justify-center gap-2 ${rowIndex && 'my-2'}`}
                  >
                  {row.map((driver, colIndex) => (
                    <DriverCard
                      key={colIndex}
                      driver={driver}
                      hasCaptainBoosterEnabled={isLeaderSelectionEnabled}
                      onSelect={scrollToPlayerList}
                      onRemove={
                        driver
                          ? () => handleRemoveDriver(driver as Player)
                          : undefined
                      }
                    />
                  ))}
                </div>
              ))}
            </div>

            {hasBench && (
              <>
                <div className="px-4 pt-4 pb-4 flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0076a9] text-white text-xs font-bold uppercase tracking-wide px-3 py-1">
                    <Bus className="h-3.5 w-3.5" />
                    Remplaçants
                  </span>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
                <div 
                  className="
                    mx-auto relative
                    pt-2
                    pb-10
                    w-full
                    h-auto
                    bg-no-repeat bg-top bg-contain
                    rounded-[0.875rem] overflow-hidden
                  "
                  style={{
                    backgroundSize: "cover",
                    backgroundImage: "linear-gradient(to bottom, rgb(0, 22, 31), rgb(0, 43, 62))"
                  }}
                >
                  {benchRowChunks.map((row, rowIndex) => (
                    <div 
                      key={`bench-${rowIndex}`}
                      className={`flex justify-center gap-2 ${rowIndex && 'my-2'}`}
                    >
                      {row.map((driver, colIndex) => (
                        <DriverCard
                          key={colIndex}
                          driver={driver}
                          onSelect={scrollToPlayerList}
                          onRemove={
                            driver
                              ? () => handleRemoveDriver(driver as Player)
                              : undefined
                          }
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
        {/* Right column: Player List and Calendar */}
        <div className="overflow-hidden rounded-[0.875rem] bg-white shadow-sm">
          <div className="bg-[rgb(0_61_96)] px-5 py-4 text-white">
            <h1 className="font-extra">Liste des coureurs</h1>
          </div>
          <div className='bg-white pt-0'>
            <div className="overflow-y-auto flex-1">
              <PlayerList
                containerId={PLAYER_LIST_ID}
                isEditable={true}
                clubs={clubs}
                drivers={players}
                selectionType={'fantasyPick'}
                isTransferMode={false}
                onSelect={handleDriverSelection}
                onRemove={handleRemoveDriver}
                selectionCheck={(driver) => driverSelectionCheck(driver)}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewTeam;
